import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Image as ImageIcon,
  Loader2,
  Mic2,
  ScanSearch,
  Send,
  Sparkles,
} from "lucide-react";
import Headerdash from "../head";
import Sidebar from "../sidebar";
import Recorder from "./recorder";
import ResultBox from "./ResultBox";
import ScoreCard from "./ScoreCard";
import SpacingBox from "./Spacing";
import ImageBox from "./ImageBox";
import useFetchData from "../fetchpostData";
import ScoreBox from "./ResponseBox";
import Notification from "./Notification";
import Timers from "../PageComponent/Timers";

const PracticeShell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 text-gray-700">
    <Headerdash />
    <Sidebar />
    <main className="min-h-screen pt-20 sm:pl-72">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </div>
    </main>
  </div>
);

const StatusPill = ({ type, children }) => {
  const styles =
    type === "success"
      ? "bg-green-50 text-green-700 ring-green-100"
      : type === "warning"
      ? "bg-amber-50 text-amber-700 ring-amber-100"
      : "bg-purple-50 text-purple-700 ring-purple-100";

  return (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase ring-1 ${styles}`}
    >
      {children}
    </span>
  );
};

export default function DescribeImagePage(props) {
  const { title } = useParams();
  const section = "describeimage";
  const practiceItemId = title;
  const post = useFetchData(section, title);

  const [originalWords, setOriginalWords] = useState("");
  const [imageLink, setImageLink] = useState("");
  const [questionTitle, setQuestionTitle] = useState("");
  const [text, setText] = useState("");
  const [isAttempted, setIsAttempted] = useState(false);
  const [message, setMessage] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [savedPractice, setSavedPractice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [result, setResult] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (post && post.media_link) {
      setOriginalWords(post.answer || "");
      setQuestionTitle(post.q_title || "Describe image");

      const fileName = encodeURIComponent(post.media_link.split("/").pop());

      fetch(`/ptedata/ptemedia/${fileName}`, { credentials: "include" })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setImageLink(data.url);
          } else {
            console.error("Failed to fetch image URL");
          }
        })
        .catch((error) => console.error("Error fetching signed URL:", error));
    }
  }, [post]);

  const savePractice = async (practiceData) => {
    try {
      const response = await fetch("/api/save-describeimage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(practiceData),
      });

      if (!response.ok) {
        throw new Error("Failed to save practice data");
      }

      return await response.json();
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  useEffect(() => {
    const highlightMatchingWords = () => {
      const words1 = originalWords.split(" ");
      const words2 = text.split(" ");

      setResult(
        words2.map((word2, index) => (
          <span
            key={`${word2}-${index}`}
            style={{
              backgroundColor: words1.includes(word2) ? "#CBFB8A" : "#E74C3C",
              marginRight: "4px",
            }}
          >
            {word2}
          </span>
        ))
      );
    };

    highlightMatchingWords();
  }, [originalWords, text]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recordedBlob) {
      setMessage("Please record audio first before submitting.");
      setNotificationType("error");
      setShowNotification(true);
      return;
    }

    setLoading(true);

    try {
      const audioForm = new FormData();
      audioForm.append("audio", recordedBlob, "recording.wav");

      const uploadRes = await fetch("/api/upload-audio", {
        method: "POST",
        body: audioForm,
      });

      if (!uploadRes.ok) {
        throw new Error("Audio upload failed");
      }

      const uploadData = await uploadRes.json();

      if (!uploadData.audioUrl) {
        throw new Error("Audio URL missing from upload response");
      }

      const transcribeForm = new FormData();
      transcribeForm.append("audio", recordedBlob, "recording.wav");

      const transRes = await fetch("/api/transcribe", {
        method: "POST",
        body: transcribeForm,
      });

      if (!transRes.ok) {
        throw new Error("Transcription request failed");
      }

      const transData = await transRes.json();

      if (!transData.text) {
        throw new Error("Transcription failed to return text");
      }

      const transcribedText = transData.text;
      setText(transcribedText);

      const practiceData = {
        practiceType: section,
        practiceItemId,
        response: {
          questionId: practiceItemId,
          audioUrl: uploadData.audioUrl,
          imageUrl: imageLink,
          audioDuration: uploadData.duration || transData.duration || 0,
          answer: transcribedText,
          transcript: transData.transcript || transcribedText,
          words: transData.words || [],
          transcriptionEngine: transData.engine,
          isAttempted: true,
        },
      };

      const result = await savePractice(practiceData);

      setMessage("Practice data saved successfully!");
      setNotificationType("success");
      setSavedPractice(result);
      setShowNotification(true);

      setTimeout(() => {
        setIsAttempted(true);
        setLoading(false);
        setSubmitted(true);
      }, 1000);
    } catch (error) {
      console.error("Submit error:", error);
      setMessage(error.message || "Something went wrong.");
      setNotificationType("error");
      setShowNotification(true);

      setLoading(false);
      setSubmitted(false);
    }
  };

  return (
    <PracticeShell>
      <section className="mb-6 flex flex-col gap-5 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-purple-700">
            Speaking practice
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-gray-900 sm:text-3xl">
            {questionTitle || "Describe image"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 sm:text-base">
            Study the visual, describe the main information clearly, and submit
            your response for scoring.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill>Medium</StatusPill>
          <StatusPill type={isAttempted ? "success" : "warning"}>
            {isAttempted ? "Attempted" : "Not attempted"}
          </StatusPill>
          <span className="inline-flex min-h-9 items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-bold text-gray-700 shadow-sm ring-1 ring-slate-200">
            <Clock3 className="h-4 w-4 text-purple-700" aria-hidden="true" />
            <Timers initialSeconds={0} />
          </span>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr),360px]">
        <div className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-4 flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <ScanSearch className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-purple-700">
                  Instructions
                </p>
                <h2 className="text-xl font-extrabold text-gray-900">
                  Observe, plan, describe
                </h2>
              </div>
            </div>

            <div className="rounded-md bg-slate-50 p-4 text-sm leading-6 text-gray-600">
              Look at the image below. In 25 seconds, please speak into the
              microphone and describe in detail what the image or graph is
              showing. You will have 40 seconds to give your response.
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr),minmax(300px,0.9fr)]">
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                  <ImageIcon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    Visual prompt
                  </p>
                  <h2 className="text-xl font-extrabold text-gray-900">
                    Inspect the image
                  </h2>
                </div>
              </div>

              {imageLink ? (
                <ImageBox link={imageLink} />
              ) : (
                <div className="flex min-h-[320px] items-center justify-center rounded-md bg-slate-50 text-sm font-semibold text-gray-500 ring-1 ring-slate-100">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
                  Loading image
                </div>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                    <Mic2 className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold uppercase text-purple-700">
                      Recording
                    </p>
                    <h2 className="text-xl font-extrabold text-gray-900">
                      Your description
                    </h2>
                  </div>
                </div>
                {recordedBlob && (
                  <span className="inline-flex items-center gap-2 rounded-md bg-green-50 px-2.5 py-1 text-xs font-bold uppercase text-green-700 ring-1 ring-green-100">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Recorded
                  </span>
                )}
              </div>

              <Recorder
                text={text}
                setText={setText}
                onRecordingComplete={(blob) => setRecordedBlob(blob)}
                {...props}
                duration={40}
                startAt={25}
                className="mt-0 mb-0 w-full max-w-xl border border-slate-200 bg-slate-50 shadow-sm"
              />
            </div>
          </section>

          <section className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
                <Sparkles className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Ready to score?
                </p>
                <p className="text-sm text-gray-500">
                  Submit after recording to upload audio and generate feedback.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" aria-hidden="true" />
                  Submit
                </>
              )}
            </button>
          </section>

          {submitted && (
            <section className="space-y-5">
              <ResultBox result={result} />
              <ScoreCard
                finalScore={savedPractice?.savedResponse?.totalScore}
                remarks={savedPractice?.savedResponse?.remarks}
                contentScore={savedPractice?.savedResponse?.contentScore}
                fluencyScore={savedPractice?.savedResponse?.fluencyScore}
                pronounctionScore={
                  savedPractice?.savedResponse?.pronounctionScore
                }
                maxScore={savedPractice?.maxScores?.overall || 90}
                traitMaxScore={savedPractice?.maxScores?.content || 90}
              />
              {savedPractice?.describeImageScore?.feedback ? (
                <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="text-sm font-semibold uppercase text-purple-700">
                    AI Feedback
                  </p>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {savedPractice.describeImageScore.feedback.overall}
                  </p>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    <div className="rounded-md bg-blue-50 p-4 ring-1 ring-blue-100">
                      <h3 className="font-bold text-blue-800">Content</h3>
                      <p className="mt-2 text-sm text-blue-900">
                        {savedPractice.describeImageScore.feedback.content}
                      </p>
                    </div>
                    <div className="rounded-md bg-green-50 p-4 ring-1 ring-green-100">
                      <h3 className="font-bold text-green-800">Fluency</h3>
                      <p className="mt-2 text-sm text-green-900">
                        {savedPractice.describeImageScore.feedback.fluency}
                      </p>
                    </div>
                    <div className="rounded-md bg-purple-50 p-4 ring-1 ring-purple-100">
                      <h3 className="font-bold text-purple-800">
                        Pronunciation
                      </h3>
                      <p className="mt-2 text-sm text-purple-900">
                        {
                          savedPractice.describeImageScore.feedback
                            .pronunciation
                        }
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 rounded-md bg-slate-50 p-4 text-sm text-gray-700 ring-1 ring-slate-100 sm:grid-cols-2 lg:grid-cols-5">
                    <span>
                      Image relevance:{" "}
                      <strong>
                        {Math.round(
                          (savedPractice.describeImageScore
                            .image_relevance_score || 0) * 100
                        )}
                        %
                      </strong>
                    </span>
                    <span>
                      Keywords:{" "}
                      <strong>
                        {Math.round(
                          (savedPractice.describeImageScore
                            .keyword_coverage_score || 0) * 100
                        )}
                        %
                      </strong>
                    </span>
                    <span>
                      WPM:{" "}
                      <strong>
                        {savedPractice.describeImageScore.fluency_metrics
                          ?.speech_rate || 0}
                      </strong>
                    </span>
                    <span>
                      Template risk:{" "}
                      <strong>
                        {Math.round(
                          (savedPractice.describeImageScore
                            .template_probability || 0) * 100
                        )}
                        %
                      </strong>
                    </span>
                    <span>
                      Template:{" "}
                      <strong>
                        {savedPractice.describeImageScore.template_detected
                          ? "Detected"
                          : "Clear"}
                      </strong>
                    </span>
                  </div>
                </div>
              ) : null}
            </section>
          )}
        </div>

        <aside className="space-y-6">
          <section className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-amber-50 text-amber-700">
                <AlertCircle className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold uppercase text-gray-500">
                  Practice tip
                </p>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Start with the overall topic, then mention key values,
                  changes, comparisons, and a concise conclusion.
                </p>
              </div>
            </div>
          </section>

          <ScoreBox
            originalWords={originalWords}
            practiceItemId={practiceItemId}
            onIsAttempted={setIsAttempted}
          />
        </aside>
      </div>

      <SpacingBox />

      {showNotification && (
        <Notification
          message={message}
          type={notificationType}
          onClose={() => setShowNotification(false)}
        />
      )}
    </PracticeShell>
  );
}
